/**
 * Better Auth Client - Admin Authentication Integration
 *
 * Provides Better Auth client setup for admin authentication operations.
 * Admin-specific configuration with role enforcement.
 * Client-side implementation for Vite SPA.
 *
 * @example
 * ```typescript
 * import { signIn, signOut, useAdminSession } from '@/lib/auth-client';
 *
 * // Sign in (admin role enforced server-side)
 * const result = await signIn.email({
 *   email: 'admin@genie.art',
 *   password: 'password'
 * });
 *
 * // Use session in React component
 * const session = useAdminSession();
 * ```
 */

import { createAuthClient } from "better-auth/client";
import { adminClient } from "better-auth/client/plugins";

// IMPORTANT: web-admin uses api-admin (not api-app) for admin-specific auth
const API_ADMIN_BASE_URL = import.meta.env.VITE_API_ADMIN_BASE_URL;
if (!API_ADMIN_BASE_URL) {
  throw new Error('VITE_API_ADMIN_BASE_URL is not configured');
}

/**
 * Better Auth Client Configuration (Admin)
 *
 * Admin authentication client with admin plugin enabled.
 * Points to api-admin service for admin-specific authentication.
 */
export const authClient = createAuthClient({
  baseURL: API_ADMIN_BASE_URL, // Points to api-admin on port 3003
  basePath: '/api/auth', // Explicit basePath for Better Auth endpoints
  plugins: [
    adminClient() // Admin capabilities
  ]
});

/**
 * Admin Authentication Operations
 *
 * Direct exports from Better Auth client for easy consumption.
 * These methods automatically handle cookies and session management.
 */
export const {
  signIn,
  signUp,
  signOut,
  getSession,
  updateUser,
  changePassword,
  requestPasswordReset,
  resetPassword,
  verifyEmail,
  $fetch // Better Fetch instance for custom API calls
} = authClient;

/**
 * Create useAdminSession hook
 *
 * Returns session getter for client-side use.
 * Note: This is a simplified wrapper. For full Better Auth session hook,
 * use the authClient directly in your components.
 */
export function useAdminSession() {
  return {
    data: null,
    error: null,
    isPending: false,
    getSession: getSession
  };
}

/**
 * Admin Session Management Utilities
 *
 * Helper functions for common admin session operations.
 */
/** Roles that can access the admin panel */
const ADMIN_PANEL_ROLES = ['admin', 'viewer'];

export const adminSessionUtils = {
  /**
   * Check if user is authenticated with admin panel access (admin or viewer)
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await getSession();
      return !!session.data?.user && ADMIN_PANEL_ROLES.includes(session.data.user.role as string);
    } catch {
      return false;
    }
  },

  /**
   * Get current admin/viewer user safely
   * Returns null if user doesn't have admin panel access
   */
  async getCurrentAdmin() {
    try {
      const session = await getSession();
      const user = session.data?.user;

      if (!user || !ADMIN_PANEL_ROLES.includes(user.role as string)) {
        return null;
      }

      return user;
    } catch {
      return null;
    }
  },

  /**
   * Verify user has admin panel access (admin or viewer)
   */
  async verifyAdminRole(): Promise<boolean> {
    try {
      const session = await getSession();
      return ADMIN_PANEL_ROLES.includes(session.data?.user?.role as string);
    } catch {
      return false;
    }
  },

  /**
   * Check if current user has write access (admin only, not viewer)
   */
  async canWrite(): Promise<boolean> {
    try {
      const session = await getSession();
      return session.data?.user?.role === 'admin';
    } catch {
      return false;
    }
  }
};

/**
 * Authentication Error Types
 *
 * Better Auth error handling with type safety.
 */
export type AuthError = {
  message: string;
  status: number;
  statusText: string;
};

/**
 * Admin Session Data Types
 *
 * TypeScript types for admin session data structure.
 */
export type AdminSessionData = {
  user: {
    id: string;
    email: string;
    name: string;
    role: 'admin' | 'viewer';
    emailVerified: boolean;
    image?: string;
    createdAt: Date;
    updatedAt: Date;
  } | null;
  session: {
    id: string;
    userId: string;
    expiresAt: Date;
    token: string;
    ipAddress?: string;
    userAgent?: string;
  } | null;
};

/**
 * Authentication Operations Type
 *
 * Type-safe interface for all auth operations.
 */
export type AuthOperations = typeof authClient;

// Re-export Better Auth types for convenience
export type { User, Session } from "better-auth/types";
